import { createResource, createSignal, Show, type JSX } from 'solid-js';
import { A } from '@solidjs/router';
import { authStore } from '@stores/session';
import { isInPortfolio, addToPortfolio, removeFromPortfolio } from '@lib/supabase/portfolio';
import { supabase } from '@lib/supabase/client';

// "Add to my strains" button on the Strain Detail page. Parallel to
// WatchToggle but distinct — portfolio drives the Diary / Start Session
// flow, watchlist drives restock alerts.

export default function PortfolioToggle(props: { strainId: string }): JSX.Element {
  // Stable composite-key source so the resource doesn't refetch on
  // every reactive tick from tracking ancestors.
  const [state, { mutate }] = createResource(
    () => `${authStore.user()?.id ?? ''}|${props.strainId}`,
    async (key) => {
      // Destructure defaults so TS narrows to `string`. Empty-string
      // fallback for `u` means signed-out callers still hit the
      // signed-out branch below; for `s` it means a malformed key
      // returns "not in portfolio" rather than throwing.
      const [u = '', s = ''] = key.split('|');
      if (!u) return { inPortfolio: false, entryId: null as string | null };
      const inPortfolio = await isInPortfolio(s);
      if (!inPortfolio) return { inPortfolio: false, entryId: null };
      const profile = authStore.profile();
      if (!profile) return { inPortfolio: true, entryId: null };
      // Fetch the entry id so we can delete without a second lookup.
      const { data } = await supabase
        .from('user_portfolio')
        .select('id')
        .eq('profile_id', profile.id)
        .eq('strain_id', s)
        .maybeSingle();
      return {
        inPortfolio: true,
        entryId: (data as { id: string } | null)?.id ?? null,
      };
    },
  );
  const [pending, setPending] = createSignal(false);

  async function toggle() {
    if (!authStore.user()) return;
    setPending(true);
    try {
      const current = state();
      if (current?.inPortfolio && current.entryId) {
        await removeFromPortfolio(current.entryId);
        mutate({ inPortfolio: false, entryId: null });
      } else {
        // addToPortfolio now returns the new entry id (or the existing
        // id on conflict), so we can mutate locally instead of
        // refetching — no pending-state flash on the toggle.
        const newId = await addToPortfolio(props.strainId);
        mutate({ inPortfolio: true, entryId: newId });
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Show
      when={authStore.user()}
      fallback={
        <A
          href="/sign-in"
          class="block text-center rounded-full bg-brand-hex text-brand-green hover:bg-brand-hex-deep py-2 text-sm font-medium transition-colors"
        >
          Sign in to save
        </A>
      }
    >
      <button
        type="button"
        onClick={toggle}
        disabled={pending() || state.loading}
        class={`w-full cursor-pointer rounded-full py-2 text-sm font-medium transition-colors ${
          state()?.inPortfolio
            ? 'bg-brand-moss/20 text-brand-green border border-brand-moss hover:bg-brand-moss/30'
            : 'bg-brand-green text-brand-cream hover:bg-brand-moss'
        } disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {state.loading
          ? '…'
          : state()?.inPortfolio
            ? 'In your strains \u2013 remove'
            : 'Add to my strains'}
      </button>
    </Show>
  );
}
